import { Drawer, DrawerContent } from '@/components/ui/drawer';
import { createContext, useContext, useState, ReactNode } from 'react';
import { cn } from '@/utils/classnames';

type DrawerContextType = {
  open: boolean;
  showDrawer: (content: ReactNode) => void;
  onClose: () => void;
};

const DrawerContext = createContext<DrawerContextType | undefined>(undefined);

export function useDrawer(): DrawerContextType {
  const context = useContext(DrawerContext);
  if (!context) {
    throw new Error('useDrawer must be used within a DrawerProvider');
  }
  return context;
}

type DrawerProviderProps = {
  children: ReactNode;
  side?: 'left' | 'right' | 'bottom' | 'top';
  width?: number;
};

const DrawerProvider = ({ children, side = 'right', width }: DrawerProviderProps) => {
  const [open, setOpen] = useState(false);

  const [content, setContent] = useState<ReactNode>(null);

  const showDrawer = (drawerContent: ReactNode) => {
    setContent(drawerContent);
    setOpen(true);
  };

  const onClose = () => {
    setOpen(false);
  };

  return (
    <DrawerContext.Provider value={{ open, showDrawer, onClose }}>
      {children}

      <Drawer open={open} onOpenChange={setOpen} direction={side ?? 'right'}>
        <DrawerContent
          className={cn(
            'drawer-scrollbar !overflow-x-hidden p-0',
            side === 'bottom'
              ? 'bg-main dark:!bg-main !overflow-hidden lg:mx-auto lg:!h-[85dvh] lg:!max-h-[85dvh] lg:w-full lg:max-w-[64rem] max-lg:!left-0 max-lg:!right-0 max-lg:!top-auto max-lg:!bottom-0 max-lg:!min-h-[65vh] max-lg:!h-[85dvh] max-lg:!max-h-[85dvh] max-lg:!w-screen max-lg:!rounded-t-[1.4rem] max-lg:!border-primary max-lg:!bg-main'
              : 'max-lg:!left-0 max-lg:!right-0 max-lg:!top-0 max-lg:!bottom-0 max-lg:!h-dvh max-lg:!max-h-dvh max-lg:!w-screen max-lg:!rounded-none',
          )}
          side={side}
          style={width ? { width } : undefined}
        >
          <div className={cn('drawer-scrollbar flex h-full w-full min-w-0 min-h-0 flex-1', side === 'bottom' ? 'overflow-hidden' : 'overflow-hidden')}>
            <div className="w-full min-w-0">{content}</div>
          </div>
        </DrawerContent>
      </Drawer>
    </DrawerContext.Provider>
  );
};

export default DrawerProvider;
